<template>
  <div class="page">
    <FindBar
      v-if="isFindBarOpen"
      ref="findBarEl"
      container-id="scrollable-document"
      @close="closeFindBar"
    />
    <div id="scrollable-document">
      <PageTopBar
        v-if="!embeddedMode"
        :has-block-active="hasBlockActive"
        :page="value"
        :title-scrolled-past="isTitleScrolledPast"
        @select-all="selectAll"
        @flush="flush"
        @find="openFindBar"
      />
      <div class="infos" @keydown="keydown">
        <TitledDocument
          ref="documentEl"
          :key="value.id"
          :debounce="debounce"
          :templates="pageTemplates"
          :value="value"
          :embedded-mode="embeddedMode"
        >
          <template #aboveTitle>
            <DefaultTypeChip v-if="!embeddedMode" kind="page" />
          </template>

          <template #properties>
            <ExtendedProperties
              class="extension-field-list"
              objectType="page"
              :objectId="value.id"
              :extensionObjectId="value.extension_row"
              :create-extension-object-callback="ensurePageExtensionObject"
            />
          </template>
        </TitledDocument>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useHead } from "@unhead/vue";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  provide,
  ref,
  toRefs,
  watch,
} from "vue";

import { Page } from "@ctrl/controller-rpc";

import { useDocument } from "@/components/document";
import DefaultTypeChip from "@/components/elements/DefaultTypeChip.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import * as inject from "@/components/inject";
import PageTopBar from "@/components/page/PageTopBar.vue";
import { pageTemplates } from "@/components/templates";
import TitledDocument from "@/components/TitledDocument.vue";
import { useExtendedProperties } from "@/composables/useExtendedProperties";
import { useScrollable } from "@/composables/useScrollable";
import { keydown } from "@/routes/object";

import ExtendedProperties from "../ExtendedProperties.vue";

export default defineComponent({
  components: {
    DefaultTypeChip,
    ExtendedProperties,
    FindBar,
    PageTopBar,
    TitledDocument,
  },
  props: {
    debounce: {
      type: Number,
      default: 5000,
    },
    previousRoute: {
      type: String,
      default: null,
    },
    value: {
      type: Object as PropType<Page>,
      required: true,
    },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
  },
  async setup(props) {
    const router = inject.router();
    const controller = inject.controller();
    const profile = inject.profile();
    const { value: page } = toRefs(props);
    const documentEl = ref<InstanceType<typeof TitledDocument>>();
    const parent = ref({ kind: "page", id: page.value.id });

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    // Register the document element as a scrollable container
    const scrollable = useScrollable(["scrollable-document"]);
    const isTitleScrolledPast = ref(false);
    let titleObserver: IntersectionObserver | null = null;

    onMounted(() => {
      scrollable.mount();
      const scrollableEl = document.getElementById("scrollable-document");
      const documentRoot = documentEl.value?.$el;
      const titleWrapperEl =
        documentRoot instanceof Element
          ? documentRoot.querySelector(".title-wrapper")
          : null;
      if (scrollableEl && titleWrapperEl) {
        titleObserver = new IntersectionObserver(
          ([entry]) => {
            isTitleScrolledPast.value = entry ? !entry.isIntersecting : false;
          },
          { root: scrollableEl, threshold: 0 }
        );
        titleObserver.observe(titleWrapperEl);
      }
    });

    onBeforeUnmount(() => {
      scrollable.unmount();
      titleObserver?.disconnect();
    });

    useHead({
      title: computed(() => page.value.title),
    });
    provide("parent", parent);
    watch(page, (page) => (parent.value = { kind: "page", id: page.id }));

    const ensurePageExtensionObject = useExtendedProperties(
      controller,
      profile,
      "page",
      () => page.value.extension_row,
      (id: string) => {
        page.value.extension_row = id;
      },
      async (id: string) => {
        await controller.page.update(
          page.value.id,
          {
            extension_row: id,
          },
          "app"
        );
      }
    );

    return {
      closeFindBar,
      documentEl,
      ensurePageExtensionObject,
      findBarEl,
      isFindBarOpen,
      isTitleScrolledPast,
      openFindBar,
      keydown,
      pageTemplates,
      ...useDocument<Page>(page, documentEl, router),
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/event.scss";

.page {
  // Anchor for the find bar.
  position: relative;
}

#scrollable-document {
  width: 100%;
}

.extension-field-list {
  margin-top: 12px;
  width: 100%;
}
</style>
